// Camera FTP support database — auto-upload, image quality, and transfer settings
// Sources: Official user manuals from Canon, Nikon, Sony, Fujifilm, Panasonic, OM System

export interface CameraModel {
  model: string;
  ftpSupported: boolean;
  /** Wi-Fi / Ethernet / WT-7 accessory, etc. */
  connectivity: string[];
  heifSupported: boolean;
  /** Firmware version required for a specific feature, if applicable */
  firmwareNote?: string;
  /** Menu path to set up the FTP server connection */
  ftpSetupPath?: string;
  /** Menu path to enable auto-transfer (shoot-and-send) */
  autoTransferPath?: string;
  /** Menu path to set file transfer target to JPEG/HEIF only */
  transferTargetPath?: string;
  /** Available transfer target options (e.g. "JPEG only", "RAW only") */
  transferTargetOptions?: string[];
  /** Menu path to set image quality / file format */
  imageQualityPath?: string;
  /** The smallest JPEG/HEIF quality tier name */
  smallestQuality?: string;
  /** Menu path to set image size / resolution */
  imageSizePath?: string;
  /** The smallest resolution option name */
  smallestSize?: string;
  /** Menu path to enable HEIF */
  heifEnablePath?: string;
  /** Official user manual URL */
  manualUrl: string;
  /** Approximate manual page range for FTP settings */
  manualFtpPages?: string;
  /** Alternative transfer method when FTP is not supported */
  alternativeMethod?: string;
  /** Companion app name */
  companionApp?: string;
  /** Note about FTP accessory required */
  ftpAccessoryNote?: string;
}

export interface CameraBrand {
  name: string;
  /** Short identifier for CSS/logic */
  id: string;
  /** Brand-specific terminology for auto-transfer */
  autoTransferTerm: string;
  /** Brand-specific terminology for transfer-target / file-type filter */
  transferTargetTerm: string;
  /** Brand color for UI accents */
  color: string;
  models: CameraModel[];
}

// ─── CANON ───────────────────────────────────────────────────────────────────

const canonModels: CameraModel[] = [
  {
    model: 'EOS R1',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: true,
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~2.5 MP)',
    imageSizePath: 'MENU → Shooting 1 → Image quality → select size',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ → Enable',
    manualUrl: 'https://cam.start.canon/en/C013/',
    manualFtpPages: 'pp. 620–660',
  },
  {
    model: 'EOS R3',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: true,
    firmwareNote: 'HEIF requires firmware 1.2+',
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~2.5 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ → Enable',
    manualUrl: 'https://cam.start.canon/en/C008/',
    manualFtpPages: 'pp. 580–620',
  },
  {
    model: 'EOS R5 Mark II',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: true,
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~4.5 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ → Enable',
    manualUrl: 'https://cam.start.canon/en/C016/',
  },
  {
    model: 'EOS R5',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    firmwareNote: 'HEIF requires firmware 1.4+. Ethernet via optional WFT-R10 grip.',
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection option → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → Image transfer → FTP transfer → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → Image transfer → FTP transfer → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~4.5 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ settings → HDR PQ: Enable',
    manualUrl: 'https://cam.start.canon/en/C006/',
    manualFtpPages: 'pp. 540–580',
  },
  {
    model: 'EOS R5 C',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    firmwareNote: 'FTP available in photo mode only. Cinema/video mode uses different workflow.',
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection option → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → Image transfer → FTP transfer → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → Image transfer → FTP transfer → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~4.5 MP)',
    smallestSize: 'S2',
    manualUrl: 'https://cam.start.canon/en/C010/',
  },
  {
    model: 'EOS R6 Mark II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~2.5 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ settings → HDR PQ: Enable',
    manualUrl: 'https://cam.start.canon/en/C012/',
  },
  {
    model: 'EOS R6',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    firmwareNote: 'HEIF requires firmware 1.5+',
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~2.5 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ settings → HDR PQ: Enable',
    manualUrl: 'https://cam.start.canon/en/C005/',
  },
  {
    model: 'EOS R7',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~3.6 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ settings → HDR PQ: Enable',
    manualUrl: 'https://cam.start.canon/en/C011/',
  },
  {
    model: 'EOS R8',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection settings → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → FTP transfer settings → Automatic transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → FTP transfer settings → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'HEIF only', 'RAW only', 'RAW+JPEG', 'RAW+HEIF'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S2 (~2.5 MP)',
    smallestSize: 'S2',
    heifEnablePath: 'MENU → Shooting 1 → HDR PQ settings → HDR PQ: Enable',
    manualUrl: 'https://cam.start.canon/en/C014/',
  },
  {
    model: 'EOS 1D X Mark III',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: false,
    ftpSetupPath: 'MENU → Communication Functions → Network settings → Connection option → FTP transfer',
    autoTransferPath: 'MENU → Communication Functions → Image transfer → FTP transfer → Auto transfer → Enable',
    transferTargetPath: 'MENU → Communication Functions → Image transfer → FTP transfer → Transfer type/size → JPEG only',
    transferTargetOptions: ['JPEG only', 'RAW only', 'RAW+JPEG'],
    imageQualityPath: 'MENU → Shooting 1 → Image quality',
    smallestQuality: 'S (~5 MP)',
    smallestSize: 'S',
    manualUrl: 'https://cam.start.canon/en/C004/',
  },
  // --- No FTP ---
  {
    model: 'EOS R10',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C009/',
  },
  {
    model: 'EOS R50',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C015/',
  },
  {
    model: 'EOS R100',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C017/',
  },
  {
    model: 'EOS 5D Mark IV',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    ftpAccessoryNote: 'FTP possible with WFT-E7 wireless file transmitter (~$850)',
    alternativeMethod: 'Use WFT-E7 accessory for FTP, or transfer via Canon Camera Connect app',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C003/',
  },
  {
    model: 'EOS 6D Mark II',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C002/',
  },
  {
    model: 'EOS 90D',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C007/',
  },
  {
    model: 'EOS Rebel T8i / 850D',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C007/',
  },
  {
    model: 'EOS Rebel T7 / 2000D',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Connect only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Canon Camera Connect app, then upload from phone',
    companionApp: 'Canon Camera Connect',
    manualUrl: 'https://cam.start.canon/en/C001/',
  },
];

// ─── NIKON ───────────────────────────────────────────────────────────────────

const nikonModels: CameraModel[] = [
  {
    model: 'Z9',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: true,
    firmwareNote: 'HEIF added in firmware 2.0+',
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic (Size priority)',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (4128×2752)',
    heifEnablePath: 'Photo Shooting Menu → Image quality → Image file format → HEIF',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/571/Z_9.html',
    manualFtpPages: 'pp. 562–600',
  },
  {
    model: 'Z8',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: true,
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (4128×2752)',
    heifEnablePath: 'Photo Shooting Menu → Image quality → Image file format → HEIF',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/596/Z_8.html',
    manualFtpPages: 'pp. 540–575',
  },
  {
    model: 'Z6 III',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (3936×2624)',
    heifEnablePath: 'Photo Shooting Menu → Image quality → Image file format → HEIF',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/614/Z_6III.html',
  },
  {
    model: 'Z7 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    firmwareNote: 'Wired Ethernet via optional WT-7 accessory',
    ftpSetupPath: 'Setup Menu → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (2768×1848)',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/539/Z_7II.html',
    manualFtpPages: 'pp. 460–490',
  },
  {
    model: 'Zf',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (3936×2624)',
    heifEnablePath: 'Photo Shooting Menu → Image quality → Image file format → HEIF',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/601/Z_f.html',
  },
  {
    model: 'Z6 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    firmwareNote: 'Wired Ethernet via optional WT-7 accessory',
    ftpSetupPath: 'Setup Menu → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (3024×2016)',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/538/Z_6II.html',
  },
  {
    model: 'Z5',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Setup Menu → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (2768×1848)',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/527/Z_5.html',
  },
  {
    model: 'Z50 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (2656×1768)',
    heifEnablePath: 'Photo Shooting Menu → Image quality → Image file format → HEIF',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/623/Z_50II.html',
  },
  {
    model: 'D6',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: false,
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (2592×1728)',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/525/D6.html',
  },
  {
    model: 'D5',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: false,
    ftpSetupPath: 'Setup Menu → Network → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Network → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Network → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (2592×1728)',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/377/D5.html',
  },
  {
    model: 'D780',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Setup Menu → Connect to FTP server → Network settings → Create profile',
    autoTransferPath: 'Setup Menu → Connect to FTP server → Options → Auto send → ON',
    transferTargetPath: 'Setup Menu → Connect to FTP server → Options → Send file as → JPEG only',
    transferTargetOptions: ['JPEG + NEF (RAW)', 'JPEG only', 'NEF (RAW) only'],
    imageQualityPath: 'Photo Shooting Menu → Image quality → JPEG basic',
    smallestQuality: 'JPEG basic',
    imageSizePath: 'Photo Shooting Menu → Image size → Small',
    smallestSize: 'Small (3072×2048)',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/518/D780.html',
  },
  // --- FTP via accessory ---
  {
    model: 'Z7',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge)'],
    heifSupported: false,
    ftpAccessoryNote: 'FTP possible with WT-7 wireless transmitter (~$700)',
    alternativeMethod: 'Use WT-7 accessory for FTP, or transfer via SnapBridge app',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/492/Z_7.html',
  },
  {
    model: 'Z6',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge)'],
    heifSupported: false,
    ftpAccessoryNote: 'FTP possible with WT-7 wireless transmitter (~$700)',
    alternativeMethod: 'Use WT-7 accessory for FTP, or transfer via SnapBridge app',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/491/Z_6.html',
  },
  {
    model: 'D850',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge)'],
    heifSupported: false,
    ftpAccessoryNote: 'FTP possible with WT-7 wireless transmitter (~$700)',
    alternativeMethod: 'Use WT-7 accessory for FTP, or transfer via SnapBridge app',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/467/D850.html',
  },
  {
    model: 'D500',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge)'],
    heifSupported: false,
    ftpAccessoryNote: 'FTP possible with WT-7 or WT-6 wireless transmitter',
    alternativeMethod: 'Use WT-7/WT-6 accessory for FTP, or transfer via SnapBridge app',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/383/D500.html',
  },
  // --- No FTP ---
  {
    model: 'Zfc',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via SnapBridge app, then upload from phone',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/543/Z_fc.html',
  },
  {
    model: 'Z50',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via SnapBridge app, then upload from phone',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/510/Z_50.html',
  },
  {
    model: 'Z30',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via SnapBridge app, then upload from phone',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/575/Z_30.html',
  },
  {
    model: 'D7500',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via SnapBridge app, then upload from phone',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/449/D7500.html',
  },
  {
    model: 'D5600',
    ftpSupported: false,
    connectivity: ['Wi-Fi (SnapBridge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via SnapBridge app, then upload from phone',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/424/D5600.html',
  },
  {
    model: 'D3500',
    ftpSupported: false,
    connectivity: ['Bluetooth only'],
    heifSupported: false,
    alternativeMethod: 'Transfer via SnapBridge (Bluetooth), then upload from phone',
    companionApp: 'SnapBridge',
    manualUrl: 'https://downloadcenter.nikonimglib.com/en/products/487/D3500.html',
  },
];

// ─── SONY ────────────────────────────────────────────────────────────────────

const sonyModels: CameraModel[] = [
  {
    model: 'Alpha 1 (A1)',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet (via USB-C adapter)'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2110/v1/en/',
  },
  {
    model: 'Alpha 9 III (A9 III)',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet (built-in)'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2350/v1/en/',
  },
  {
    model: 'Alpha 9 II (A9 II)',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet (via USB-C adapter)'],
    heifSupported: false,
    firmwareNote: 'Uses older menu system. First Sony Alpha with built-in FTP.',
    ftpSetupPath: 'Network → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → FTP Transfer Func. → Target File → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'RAW Only', 'RAW & JPEG'],
    imageQualityPath: 'Camera 1 → Quality/Image Size → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Camera 1 → Quality/Image Size → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    manualUrl: 'https://helpguide.sony.net/ilc/1960/v1/en/',
  },
  {
    model: 'Alpha 7R V (A7R V)',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2250/v1/en/',
  },
  {
    model: 'Alpha 7 IV (A7 IV)',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2210/v1/en/',
  },
  {
    model: 'Alpha 7S III (A7S III)',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    firmwareNote: 'First Sony Alpha with new menu system, but predates HEIF support.',
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'RAW Only', 'RAW & JPEG'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    manualUrl: 'https://helpguide.sony.net/ilc/2040/v1/en/',
  },
  {
    model: 'Alpha 7C II (A7C II)',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2320/v1/en/',
  },
  {
    model: 'Alpha 6700 (A6700)',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2310/v1/en/',
  },
  {
    model: 'ZV-E1',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG', 'RAW & HEIF'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2270/v1/en/',
  },
  {
    model: 'ZV-E10 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network → Transfer/Remote → FTP Transfer Func. → Server Setting',
    autoTransferPath: 'Network → Transfer/Remote → FTP Transfer Func. → Auto Transfer → On',
    transferTargetPath: 'Network → Transfer/Remote → FTP Transfer Func. → Target File for Trans. → JPEG Only',
    transferTargetOptions: ['JPEG Only', 'HEIF Only', 'RAW Only', 'RAW & JPEG'],
    imageQualityPath: 'Shooting → Image Quality/Rec → JPEG Quality → Light',
    smallestQuality: 'Light + Small',
    imageSizePath: 'Shooting → Image Quality/Rec → JPEG Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Shooting → Image Quality/Rec → File Format → HEIF(4:2:0)',
    manualUrl: 'https://helpguide.sony.net/ilc/2410/v1/en/',
  },
  // --- No FTP ---
  {
    model: 'Alpha 7R IV (A7R IV)',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Imaging Edge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/1960/v1/en/',
  },
  {
    model: 'Alpha 7 III (A7 III)',
    ftpSupported: false,
    connectivity: ['Wi-Fi (PlayMemories only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/1720/v1/en/',
  },
  {
    model: 'Alpha 7C (A7C)',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Imaging Edge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/2010/v1/en/',
  },
  {
    model: 'Alpha 6600 (A6600)',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Imaging Edge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/1940/v1/en/',
  },
  {
    model: 'Alpha 6400 (A6400)',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Imaging Edge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/1810/v1/en/',
  },
  {
    model: 'Alpha 6100 (A6100)',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Imaging Edge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/1900/v1/en/',
  },
  {
    model: 'ZV-E10',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Imaging Edge only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Imaging Edge Mobile app, then upload from phone',
    companionApp: 'Imaging Edge Mobile',
    manualUrl: 'https://helpguide.sony.net/ilc/2100/v1/en/',
  },
];

// ─── FUJIFILM ────────────────────────────────────────────────────────────────

const fujifilmModels: CameraModel[] = [
  {
    model: 'X-H2S',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    firmwareNote: 'HEIF requires firmware 3.00+. Ethernet via optional FT-XH grip.',
    ftpSetupPath: 'Network/USB Setting → FTP Transfer → FTP Server Settings',
    autoTransferPath: 'Network/USB Setting → FTP Transfer → Auto Transfer → ON',
    transferTargetPath: 'Network/USB Setting → FTP Transfer → File Type to Transfer → JPEG ONLY',
    transferTargetOptions: ['JPEG ONLY', 'RAW ONLY', 'JPEG+RAW'],
    imageQualityPath: 'Image Quality Setting → Image Quality → JPEG (Basic)',
    smallestQuality: 'Basic, S resolution',
    imageSizePath: 'Image Quality Setting → Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Image Quality Setting → Image Format → HEIF',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-h2s/',
  },
  {
    model: 'X-H2',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    firmwareNote: 'HEIF requires firmware 2.00+. Ethernet via optional FT-XH grip.',
    ftpSetupPath: 'Network/USB Setting → FTP Transfer → FTP Server Settings',
    autoTransferPath: 'Network/USB Setting → FTP Transfer → Auto Transfer → ON',
    transferTargetPath: 'Network/USB Setting → FTP Transfer → File Type to Transfer → JPEG ONLY',
    transferTargetOptions: ['JPEG ONLY', 'RAW ONLY', 'JPEG+RAW'],
    imageQualityPath: 'Image Quality Setting → Image Quality → JPEG (Basic)',
    smallestQuality: 'Basic, S resolution',
    imageSizePath: 'Image Quality Setting → Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Image Quality Setting → Image Format → HEIF',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-h2/',
  },
  {
    model: 'X-T5',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: true,
    ftpSetupPath: 'Network/USB Setting → FTP Transfer → FTP Server Settings',
    autoTransferPath: 'Network/USB Setting → FTP Transfer → Auto Transfer → ON',
    transferTargetPath: 'Network/USB Setting → FTP Transfer → File Type to Transfer → JPEG ONLY',
    transferTargetOptions: ['JPEG ONLY', 'RAW ONLY', 'JPEG+RAW'],
    imageQualityPath: 'Image Quality Setting → Image Quality → JPEG (Basic)',
    smallestQuality: 'Basic, S resolution',
    imageSizePath: 'Image Quality Setting → Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Image Quality Setting → Image Format → HEIF',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-t5/',
  },
  {
    model: 'GFX 100 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi', 'Ethernet'],
    heifSupported: true,
    ftpSetupPath: 'Network/USB Setting → FTP Transfer → FTP Server Settings',
    autoTransferPath: 'Network/USB Setting → FTP Transfer → Auto Transfer → ON',
    transferTargetPath: 'Network/USB Setting → FTP Transfer → File Type to Transfer → JPEG ONLY',
    transferTargetOptions: ['JPEG ONLY', 'RAW ONLY', 'JPEG+RAW'],
    imageQualityPath: 'Image Quality Setting → Image Quality → JPEG (Basic)',
    smallestQuality: 'Basic, S resolution',
    imageSizePath: 'Image Quality Setting → Image Size → S',
    smallestSize: 'S (Small)',
    heifEnablePath: 'Image Quality Setting → Image Format → HEIF',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/gfx100ii/',
  },
  {
    model: 'GFX 100S',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network/USB Setting → FTP Transfer → FTP Server Settings',
    autoTransferPath: 'Network/USB Setting → FTP Transfer → Auto Transfer → ON',
    transferTargetPath: 'Network/USB Setting → FTP Transfer → File Type to Transfer → JPEG ONLY',
    transferTargetOptions: ['JPEG ONLY', 'RAW ONLY', 'JPEG+RAW'],
    imageQualityPath: 'Image Quality Setting → Image Quality → JPEG (Basic)',
    smallestQuality: 'Basic, S resolution',
    imageSizePath: 'Image Quality Setting → Image Size → S',
    smallestSize: 'S (Small)',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/gfx100s/',
  },
  {
    model: 'GFX 50S II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network/USB Setting → FTP Transfer → FTP Server Settings',
    autoTransferPath: 'Network/USB Setting → FTP Transfer → Auto Transfer → ON',
    transferTargetPath: 'Network/USB Setting → FTP Transfer → File Type to Transfer → JPEG ONLY',
    transferTargetOptions: ['JPEG ONLY', 'RAW ONLY', 'JPEG+RAW'],
    imageQualityPath: 'Image Quality Setting → Image Quality → JPEG (Basic)',
    smallestQuality: 'Basic, S resolution',
    imageSizePath: 'Image Quality Setting → Image Size → S',
    smallestSize: 'S (Small)',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/gfx50s2/',
  },
  // --- No FTP ---
  {
    model: 'X-T4',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Remote only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Fujifilm Camera Remote app, then upload from phone',
    companionApp: 'Fujifilm Camera Remote',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-t4/',
  },
  {
    model: 'X-T3',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Remote only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Fujifilm Camera Remote app, then upload from phone',
    companionApp: 'Fujifilm Camera Remote',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-t3/',
  },
  {
    model: 'X-T30 II',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Remote only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Fujifilm Camera Remote app, then upload from phone',
    companionApp: 'Fujifilm Camera Remote',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-t30m2/',
  },
  {
    model: 'X-S20',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Remote only)'],
    heifSupported: true,
    alternativeMethod: 'Transfer to phone via Fujifilm Camera Remote app, then upload from phone',
    companionApp: 'Fujifilm Camera Remote',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-s20/',
  },
  {
    model: 'X-S10',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Camera Remote only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Fujifilm Camera Remote app, then upload from phone',
    companionApp: 'Fujifilm Camera Remote',
    manualUrl: 'https://fujifilm-dsc.com/en/manual/x-s10/',
  },
];

// ─── PANASONIC / LUMIX ───────────────────────────────────────────────────────

const panasonicModels: CameraModel[] = [
  {
    model: 'Lumix S5 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS (Extra Small)',
    imageSizePath: 'Photo → Image Quality → Picture Size → XS',
    smallestSize: 'XS',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-S5M2_manual_en.html',
  },
  {
    model: 'Lumix S5 IIX',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS (Extra Small)',
    imageSizePath: 'Photo → Image Quality → Picture Size → XS',
    smallestSize: 'XS',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-S5M2X_manual_en.html',
  },
  {
    model: 'Lumix S1',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    firmwareNote: 'Wired Ethernet via DMW-SFU2 software key + USB-Ethernet adapter',
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS',
    imageSizePath: 'Photo → Image Quality → Picture Size → S',
    smallestSize: 'S',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-S1_manual_en.html',
  },
  {
    model: 'Lumix S1R',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    firmwareNote: 'Wired Ethernet via DMW-SFU2 software key + USB-Ethernet adapter',
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS',
    imageSizePath: 'Photo → Image Quality → Picture Size → S',
    smallestSize: 'S',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-S1R_manual_en.html',
  },
  {
    model: 'Lumix S1H',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    firmwareNote: 'Wired Ethernet via DMW-SFU2 software key + USB-Ethernet adapter',
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS',
    imageSizePath: 'Photo → Image Quality → Picture Size → S',
    smallestSize: 'S',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-S1H_manual_en.html',
  },
  {
    model: 'Lumix S5',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS',
    imageSizePath: 'Photo → Image Quality → Picture Size → S',
    smallestSize: 'S',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-S5_manual_en.html',
  },
  {
    model: 'Lumix GH6',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS',
    imageSizePath: 'Photo → Image Quality → Picture Size → XS',
    smallestSize: 'XS',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-GH6_manual_en.html',
  },
  {
    model: 'Lumix GH5 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → Standard',
    smallestQuality: 'Standard',
    imageSizePath: 'Photo → Image Quality → Picture Size → S',
    smallestSize: 'S',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-GH5M2_manual_en.html',
  },
  {
    model: 'Lumix G9 II',
    ftpSupported: true,
    connectivity: ['Wi-Fi'],
    heifSupported: false,
    ftpSetupPath: 'Network → Wi-Fi → Wi-Fi Function → FTP Server Settings',
    autoTransferPath: 'Network → Wi-Fi → Wi-Fi Function → Send Images While Recording → ON',
    transferTargetPath: 'Network → Wi-Fi → Wi-Fi Function → File Type → JPEG',
    transferTargetOptions: ['JPEG', 'RAW', 'JPEG+RAW'],
    imageQualityPath: 'Photo → Image Quality → Quality → XS',
    smallestQuality: 'XS',
    imageSizePath: 'Photo → Image Quality → Picture Size → XS',
    smallestSize: 'XS',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-G9M2_manual_en.html',
  },
  // --- No FTP ---
  {
    model: 'Lumix GH5',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Lumix Sync only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Lumix Sync app, then upload from phone',
    companionApp: 'Lumix Sync',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-GH5_manual_en.html',
  },
  {
    model: 'Lumix G9',
    ftpSupported: false,
    connectivity: ['Wi-Fi (Lumix Sync only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via Lumix Sync app, then upload from phone',
    companionApp: 'Lumix Sync',
    manualUrl: 'https://panasonic.jp/dc/p-db/DC-G9_manual_en.html',
  },
];

// ─── OM SYSTEM / OLYMPUS ─────────────────────────────────────────────────────

const omSystemModels: CameraModel[] = [
  {
    model: 'OM-1 Mark II',
    ftpSupported: false,
    connectivity: ['Wi-Fi (OM Image Share only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via OM Image Share app, then upload from phone',
    companionApp: 'OM Image Share',
    manualUrl: 'https://cs.olympus-imaging.jp/en/support/man/om/om1mk2/',
  },
  {
    model: 'OM-1',
    ftpSupported: false,
    connectivity: ['Wi-Fi (OM Image Share only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via OM Image Share app, then upload from phone',
    companionApp: 'OM Image Share',
    manualUrl: 'https://cs.olympus-imaging.jp/en/support/man/om/om1/',
  },
  {
    model: 'OM-5',
    ftpSupported: false,
    connectivity: ['Wi-Fi (OM Image Share only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via OM Image Share app, then upload from phone',
    companionApp: 'OM Image Share',
    manualUrl: 'https://cs.olympus-imaging.jp/en/support/man/om/om5/',
  },
  {
    model: 'E-M1 Mark III',
    ftpSupported: false,
    connectivity: ['Wi-Fi (OI.Share only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via OI.Share app, then upload from phone',
    companionApp: 'OI.Share',
    manualUrl: 'https://cs.olympus-imaging.jp/en/support/man/dig/em1m3/',
  },
  {
    model: 'E-M1X',
    ftpSupported: false,
    connectivity: ['Wi-Fi (OI.Share only)'],
    heifSupported: false,
    alternativeMethod: 'Transfer to phone via OI.Share app, then upload from phone',
    companionApp: 'OI.Share',
    manualUrl: 'https://cs.olympus-imaging.jp/en/support/man/dig/em1x/',
  },
];

// ─── BRAND DEFINITIONS ──────────────────────────────────────────────────────

export const CAMERA_BRANDS: CameraBrand[] = [
  {
    name: 'Canon',
    id: 'canon',
    autoTransferTerm: 'Automatic transfer',
    transferTargetTerm: 'Transfer type/size',
    color: '#BC0024',
    models: canonModels,
  },
  {
    name: 'Nikon',
    id: 'nikon',
    autoTransferTerm: 'Auto send',
    transferTargetTerm: 'Send file as',
    color: '#F5D100',
    models: nikonModels,
  },
  {
    name: 'Sony',
    id: 'sony',
    autoTransferTerm: 'Auto Transfer',
    transferTargetTerm: 'Target File for Trans.',
    color: '#000000',
    models: sonyModels,
  },
  {
    name: 'Fujifilm',
    id: 'fujifilm',
    autoTransferTerm: 'Auto Transfer',
    transferTargetTerm: 'File Type to Transfer',
    color: '#86B817',
    models: fujifilmModels,
  },
  {
    name: 'Panasonic',
    id: 'panasonic',
    autoTransferTerm: 'Send Images While Recording',
    transferTargetTerm: 'File Type',
    color: '#0068B5',
    models: panasonicModels,
  },
  {
    name: 'OM System',
    id: 'omsystem',
    autoTransferTerm: 'N/A',
    transferTargetTerm: 'N/A',
    color: '#1B3C8F',
    models: omSystemModels,
  },
];
